import React, { useEffect, useState } from "react";
import { adminApiService } from "services/api";

const initialForm = {
  code: "",
  discount_type: "percentage",
  discount_value: "",
  min_order_value: "",
  max_uses: "",
  expires_at: "",
  is_active: true,
};

const ManageCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);

  const fetchCoupons = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiService.getAllCoupons();
      if (!res.ok) throw new Error("Failed to fetch coupons");
      const data = await res.json();
      setCoupons(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Add Coupon
  const handleAddCoupon = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);
    try {
      const payload = {
        ...form,
        discount_value: parseFloat(form.discount_value),
        min_order_value: form.min_order_value ? parseFloat(form.min_order_value) : 0,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null,
      };
      const res = await adminApiService.addCoupon(payload);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Failed to add coupon");
      }
      setShowAddModal(false);
      setForm(initialForm);
      fetchCoupons();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Edit Coupon
  const openEditModal = (coupon) => {
    setEditId(coupon.id);
    setForm({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      min_order_value: coupon.min_order_value || "",
      max_uses: coupon.max_uses === null ? "" : coupon.max_uses,
      expires_at: coupon.expires_at ? coupon.expires_at.slice(0, 16) : "",
      is_active: coupon.is_active,
    });
    setFormError(null);
    setShowEditModal(true);
  };

  const handleEditCoupon = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);
    try {
      const payload = {
        ...form,
        discount_value: parseFloat(form.discount_value),
        min_order_value: form.min_order_value ? parseFloat(form.min_order_value) : 0,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null,
      };
      const res = await adminApiService.updateCoupon(editId, payload);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Failed to update coupon");
      }
      setShowEditModal(false);
      setForm(initialForm);
      setEditId(null);
      fetchCoupons();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white dark:bg-navy-800 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-primary dark:text-white">Manage Coupons</h1>
        <button
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition-colors"
          onClick={() => { setShowAddModal(true); setForm(initialForm); }}
        >
          + Add Coupon
        </button>
      </div>
      {loading ? (
        <div className="text-primary">Loading coupons...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : coupons.length === 0 ? (
        <div className="text-gray-500 dark:text-gray-300">No coupons found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-100 dark:bg-navy-700">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-white uppercase">Code</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-white uppercase">Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-white uppercase">Value</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-white uppercase">Min Order</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-white uppercase">Max Uses</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-white uppercase">Used</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-white uppercase">Expires</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-white uppercase">Active</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-navy-800 divide-y divide-gray-200 dark:divide-gray-700">
              {coupons.map((coupon) => (
                <tr key={coupon.id}>
                  <td className="px-4 py-2 font-mono text-primary font-semibold">{coupon.code}</td>
                  <td className="px-4 py-2 capitalize">{coupon.discount_type}</td>
                  <td className="px-4 py-2">{coupon.discount_type === "percentage" ? `${coupon.discount_value}%` : `৳${coupon.discount_value}`}</td>
                  <td className="px-4 py-2">৳{coupon.min_order_value}</td>
                  <td className="px-4 py-2">{coupon.max_uses ?? "∞"}</td>
                  <td className="px-4 py-2">{coupon.uses_count}</td>
                  <td className="px-4 py-2">{coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString() : "-"}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${coupon.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {coupon.is_active ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="text-primary hover:underline mr-2"
                      onClick={() => openEditModal(coupon)}
                    >
                      Edit
                    </button>
                    {/* Delete button can go here */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Coupon Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-navy-800 rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-primary"
              onClick={() => setShowAddModal(false)}
              disabled={formLoading}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4 text-primary dark:text-white">Add Coupon</h2>
            <form onSubmit={handleAddCoupon} className="space-y-4">
              {/* form fields (same as before) */}
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input
                  type="text"
                  name="code"
                  value={form.code}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Discount Type</label>
                <select
                  name="discount_type"
                  value={form.discount_type}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-primary"
                  required
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed (৳)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Discount Value</label>
                <input
                  type="number"
                  name="discount_value"
                  value={form.discount_value}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-primary"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Minimum Order Value</label>
                <input
                  type="number"
                  name="min_order_value"
                  value={form.min_order_value}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-primary"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Uses</label>
                <input
                  type="number"
                  name="max_uses"
                  value={form.max_uses}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-primary"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Expires At</label>
                <input
                  type="datetime-local"
                  name="expires_at"
                  value={form.expires_at}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-primary"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleFormChange}
                  className="mr-2"
                />
                <label className="text-sm font-medium">Active</label>
              </div>
              {formError && <div className="text-red-500 text-sm">{formError}</div>}
              <button
                type="submit"
                className="w-full bg-primary text-white py-2 rounded hover:bg-primary-dark transition-colors disabled:opacity-60"
                disabled={formLoading}
              >
                {formLoading ? "Adding..." : "Add Coupon"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Coupon Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-navy-800 rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-primary"
              onClick={() => setShowEditModal(false)}
              disabled={formLoading}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4 text-primary dark:text-white">Edit Coupon</h2>
            <form onSubmit={handleEditCoupon} className="space-y-4">
              {/* form fields (same as before) */}
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input
                  type="text"
                  name="code"
                  value={form.code}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Discount Type</label>
                <select
                  name="discount_type"
                  value={form.discount_type}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-primary"
                  required
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed (৳)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Discount Value</label>
                <input
                  type="number"
                  name="discount_value"
                  value={form.discount_value}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-primary"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Minimum Order Value</label>
                <input
                  type="number"
                  name="min_order_value"
                  value={form.min_order_value}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-primary"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Uses</label>
                <input
                  type="number"
                  name="max_uses"
                  value={form.max_uses}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-primary"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Expires At</label>
                <input
                  type="datetime-local"
                  name="expires_at"
                  value={form.expires_at}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-primary"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleFormChange}
                  className="mr-2"
                />
                <label className="text-sm font-medium">Active</label>
              </div>
              {formError && <div className="text-red-500 text-sm">{formError}</div>}
              <button
                type="submit"
                className="w-full bg-primary text-white py-2 rounded hover:bg-primary-dark transition-colors disabled:opacity-60"
                disabled={formLoading}
              >
                {formLoading ? "Saving..." : "Save Changes"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCoupons; 